import { ButtonType,ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../codegen/Localize';
import { Set } from '../../ts/models/Set';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import _ = require('underscore');
import { DeprecatedInfoButton } from '~src/app/spaces/Header/DeprecatedInfoButton';
import { showAddCredentialAuthModeDialog } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { AddServiceAccountFileAction } from '~src/connectivity/services/AddServiceAccountFileCredentialAction';

import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { OAuthCredentials } from '../../connectivity/resources/OAuthCredentials';
import { AddOAuthCredentialAction } from '../../connectivity/services/AddOAuthCredentialAction';
import { OAuthCredentialsService } from '../../connectivity/services/OAuthCredentialsService';
import { ListResult } from '../../ts/fetchers/ListResult';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '../../ts/ServerApi/types/IOAuthCredential';
import { showDeleteSavedPasswordActionDialog } from '../../ts/siteManagement/services/DeleteMyCredentialsAction';
import { showDeleteOAuthCredentialsDialog } from '../../ts/siteManagement/services/DeleteOAuthCredentialsAction';
import { CredentialTypeFilter } from '../services/filters/CredentialTypeFilter';
import { AddNewOAuthSavedCredentialBody, AddNewOAuthSavedCredentialBodyProps, AddNewOAuthSavedCredentialBodyState } from './AddNewOAuthSavedCredentialBody';


export interface OAuthCredentialViewModel  {
  credential: IOAuthCredential;
  test: {
    // Determine if test button is displayed to customers, as of Q4 2016, we disable them for
    // generic-auth-based WDC credentials. TFS 546010
    enabled?: boolean;
  };
}

export interface OAuthCredentialsList {
  typeInfo: IOAuthCredentialTypeInfo;
  addEnabled: boolean;
  credentials?: OAuthCredentialViewModel[];
  serviceAccountCredentials?: OAuthCredentialViewModel[];
}

export type SavedCredentialsForDataSourceSettingProps = {
  serverUser: IServerUserInfo;
}

const createOAuthCredentialLists = (credentials?: IOAuthCredential[]): OAuthCredentialsList[] => {
  const convertToCredentialViewModel = (credential: IOAuthCredential): OAuthCredentialViewModel => {
    return {credential: credential, test: { enabled: OAuthCredentialsService.supportsTestConnection(credential.type, credential) }};
  };

  const credentialVMGroupsMap = _.groupBy(credentials, c => c.type);

  // Iterate through all OAuth types, if any is enabled, put it in the result
  return _.compact(_.map(OAuthCredentialsService.getAllOAuthCredentialTypes().filter(typeInfo => typeInfo.type !== 'slack'),
    (typeInfo: IOAuthCredentialTypeInfo) => {
      const allCredentials = _.map(credentialVMGroupsMap[typeInfo.type], convertToCredentialViewModel);
      const oauthCredentialsList: OAuthCredentialsList = {
        typeInfo: typeInfo,
        addEnabled: OAuthCredentials.canAddForConnectionType(typeInfo),
        // Create a list of JUST the oauth credentials, remove the values from the credentials list that
        // are marked as service account credentials.
        credentials: _.filter(allCredentials, c => !c.credential.serviceAccountCredential),
        // Create a list of JUST the service account crednetials, by removing the non-service account credentials.
        serviceAccountCredentials: _.filter(allCredentials, c => c.credential.serviceAccountCredential)
      };

      return (oauthCredentialsList.addEnabled || oauthCredentialsList.credentials.length > 0) ? oauthCredentialsList : null;
    })
  );
};

export function SavedCredentialsForDataSourceSetting(props: SavedCredentialsForDataSourceSettingProps): JSX.Element {
  const [credentialsLists, setCredentialsLists] = React.useState(createOAuthCredentialLists() as OAuthCredentialsList[]);

  async function getCredentialsLists() {
    const credentialsListResult: ListResult<IOAuthCredential> = await SliceableHelper.fetchAll(OAuthCredentials.all());
    setCredentialsLists(createOAuthCredentialLists(credentialsListResult.items));
  }

  React.useEffect(() => {
    getCredentialsLists();
  }, []);


  async function OnAddCredentialForSelectedAuthMode(props: any, state: any, updateDialogState: any, type: string){
    if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_OAuth()) {
        // if this is just a normal OAuth credential we are adding, then call
        // into attemptAuth to start the oauth flow.
        AddOAuthCredentialAction.attemptAuth(type).then(getCredentialsLists);
      } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount()) {
        // otherwise if this is a service account file credential we are adding,
        // try and parse the file and then store the new credential.
        await AddServiceAccountFileAction
        .addServiceAccountFile(state.connAuthState.file, updateDialogState);
        getCredentialsLists();
      }
  }

  async function OnAddCredentialForServerRole(props: AddNewOAuthSavedCredentialBodyProps, state: AddNewOAuthSavedCredentialBodyState, typeInfo: IOAuthCredentialTypeInfo) {
    const server = state.server;
    var instanceUrl;
    if (server.startsWith('https://')){
      instanceUrl = server;
    } else if (server.startsWith('http://')){
      instanceUrl = server.replace('http://','https://');
    } else {
      instanceUrl = 'https://' + server;
    }
    AddOAuthCredentialAction.attemptAuth(typeInfo.type, instanceUrl,
    state.role).then(getCredentialsLists);
  }

  const getOauthServerRoleAndRedirect = (typeInfo: IOAuthCredentialTypeInfo) => {

    // TODO: TFSID 1256282 change the below to use the REACT dialog for
    // confirm action dialog rather than the legacy angular one... guess this was already kinda done?
    // but we can change this to be more consistent with the new object I created...
    openReactActionDialog<AddNewOAuthSavedCredentialBodyProps, AddNewOAuthSavedCredentialBodyState, any>({
      title:  Messages.addNewSavedCredentialTitle(),
      bodyComponent: AddNewOAuthSavedCredentialBody,
      initialBodyProps: {
        prompt: Messages.customAuthenticationSettings(),
        supportsOAuthRole: (typeInfo.type === 'snowflake')
      },
      initialDialogState: { server: '', role: '' },
      actionName: Messages.add(),
      dismissDialogOnActionFailed: true,
      action: (props, state) => {return OnAddCredentialForServerRole(props, state, typeInfo);},
      actionDisabled: (_bodyProps: AddNewOAuthSavedCredentialBodyProps, dialogState: AddNewOAuthSavedCredentialBodyState) => dialogState.server.length === 0,
      dialogWidth: TabDialogWidth.width500px,
  });
};

  const addCredential = async (typeInfo: IOAuthCredentialTypeInfo) => {
    if (typeInfo.type === 'snowflake' || typeInfo.supportsCustomDomain === true) {
      getOauthServerRoleAndRedirect(typeInfo);
    }  else if (typeInfo.type === 'bigquery') {
      // TFS1243460 For GBQ, we now support Service Account file credentials AND OAuth, so the user must pick the mode.
      showAddCredentialAuthModeDialog(typeInfo.type, OnAddCredentialForSelectedAuthMode);
    } else {
      await AddOAuthCredentialAction.attemptAuth(typeInfo.type);
      getCredentialsLists();
    }
  };

  const deleteCredential = async (oauthCredential: OAuthCredentialViewModel) => {
    await showDeleteOAuthCredentialsDialog(Set.fromIdItemArray([oauthCredential.credential]));
    getCredentialsLists();
  };

  const deleteAllCredentials = async () => {
    await showDeleteSavedPasswordActionDialog();
    getCredentialsLists();
  };

  return (
    <table>
    <tbody className={UserSettingsStyle.tbody}>
        {/* Saved Credentials Section */}
        <tr>
          <td className={UserSettingsStyle.labelText}>
              {Messages.savedCredentials()}
          </td>
          <td>
            <div className={UserSettingsStyle.securityButton}>
              <ButtonWidget testId= "saved-credentials-delete-all"
                buttonType={ ButtonType.Outline }
                handleClick={ deleteAllCredentials }>
                { Messages.deleteAllCredentialsAndPasswords_action() }
              </ButtonWidget>
            </div>
          </td>
        </tr>
        {credentialsLists.length > 0 &&
          <tr data-test-id="saved-credentials-section">
            <td></td>
            <td className={UserSettingsStyle.detailsValue} colSpan={2}>
              <div className="tb-oauth-table tb-enable-selection">
              {credentialsLists.map(credentialList =>
                <div className="tb-oauth-type" key={credentialList.typeInfo.type}data-tb-test-id={'tb-credentials-' + credentialList.typeInfo.type + '-parent'}>
                  <div className="tb-oauth-type-header">
                    <span className="tb-oauth-type-title" data-tb-test-id={'tb-credential-' + credentialList.typeInfo.type + '-title'}>
                      {CredentialTypeFilter.filter(credentialList.typeInfo)}
                      <ConnectorInfoButton typeInfo={credentialList.typeInfo}/>
                    </span>
                    {credentialList.addEnabled &&
                      <a className="tb-oauth-type-add"
                      data-tb-test-id={'tb-credential-' + credentialList.typeInfo.type + '-add'}
                      href={AddOAuthCredentialAction.href(credentialList.typeInfo)}
                      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {addCredential(credentialList.typeInfo); event.preventDefault();}}>
                        {Messages.add()}
                      </a>}
                  </div>
                  {credentialList.serviceAccountCredentials.length > 0 &&
                    <div className="tb-credential-type-header">
                      <span className="tb-credential-type-title">
                        {Messages.savedCredentials_ServiceAccount()}
                      </span>
                    </div>
                  }
                  {credentialList.serviceAccountCredentials.map(credRow =>
                    <SavedCredentialsTypeRow credRow={credRow} deleteCredential={deleteCredential} key={credRow.credential.id}/>
                  )}
                  {credentialList.serviceAccountCredentials.length > 0 &&
                    <div className="tb-credential-type-header">
                      <span className="tb-credential-type-title">
                        {Messages.savedCredentials_OAuth()}
                      </span>
                    </div>
                  }
                  {credentialList.credentials.map(credRow =>
                    <SavedCredentialsTypeRow credRow={credRow} deleteCredential={deleteCredential} key={credRow.credential.id}/>
                  )}
                </div>
              )}
              </div>
            </td>
          </tr>}
    </tbody>
    </table>
  );
}


export type ConnectorInfoButtonProps = {
  typeInfo: IOAuthCredentialTypeInfo;
}

export function ConnectorInfoButton(props: ConnectorInfoButtonProps,   openDetail: () => void): JSX.Element {
  if (props.typeInfo.type === 'google-sheets') {
    return (<DeprecatedInfoButton testId={`${props.typeInfo.type}`} onClick={openDetail} pre_link={Messages.deprecate_gsheet_pre_link()} link={Messages.deprecate_gsheet_instruction_link()} title={Messages.deprecate_gsheet_title()} link_descriptor={Messages.deprecate_gsheet_link_descriptor()}/>
    );
  }
  else{
    return (<div></div>);
  }
}

export type SavedCredentialsTypeRowProps = {
  credRow: OAuthCredentialViewModel;
  deleteCredential: (oauthCredential: OAuthCredentialViewModel) => Promise<void>;
}

export function SavedCredentialsTypeRow(props: SavedCredentialsTypeRowProps): JSX.Element {
  const [isTestInProgress, setIsTestInProgress] = React.useState(false);
  const [isTestSuccessful, setIsTestSuccessful] = React.useState(undefined);
  const credRow = props.credRow;

  const testCredential = (oauthCredential: OAuthCredentialViewModel) => {
    setIsTestInProgress(true);
    const handleTestResult = (success: any) => {
      setIsTestSuccessful(!!success);
      setIsTestInProgress(false);
    };
    OAuthCredentials.test(oauthCredential.credential.id)
      .then(handleTestResult, () => handleTestResult(false));
  };

  return (
    <div className="tb-oauth-type-row">
      <div className="tb-oauth-type-row-details">
        <span className="tb-oauth-type-username" title={credRow.credential.username} data-tb-test-id={'tb-credential-' + credRow.credential.id + '-username'}>
          <span>{credRow.credential.username}<br></br></span>
          {credRow.credential.instanceUrl && <span>{credRow.credential.instanceUrl}<br></br></span>}
          {credRow.credential.role && <span>{credRow.credential.role}<br></br></span>}
        </span>
        {/* eslint-disable jsx-a11y/anchor-is-valid */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        <a className="tb-oauth-type-delete"
          data-tb-test-id={'tb-credential-' + credRow.credential.id + '-delete-button'}
          onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {props.deleteCredential(credRow); event.preventDefault();}}>
            {Messages.deleteMessage()}
        </a>}
        {credRow.test.enabled &&
        /* eslint-disable jsx-a11y/anchor-is-valid */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
          <a className="tb-oauth-type-test"
            data-tb-test-id={'tb-credential-' + credRow.credential.id + '-test-button'}
            onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {testCredential(credRow); event.preventDefault();}}>
              {Messages.test()}
          </a>}
      </div>
      <div className="tb-oauth-test-results">
        {isTestInProgress && <span className="tb-icon-spinner tb-lr-padded-narrow"></span>}
        {!isTestInProgress && isTestSuccessful === true &&
          <span className="tb-oauth-test-message" data-tb-test-id={'tb-credential-' + credRow.credential.id + '-testing-message'}>
            {Messages.oauthCredentialTestSuccess()}
          </span>}
        {!isTestInProgress && isTestSuccessful === false &&
        <span className="tb-oauth-test-message tb-error" data-tb-test-id={'tb-credential-' + credRow.credential.id + '-error-message'}>
          {Messages.oauthCredentialTestError()}
        </span>}
      </div>
    </div>
  );
}
